/**
 * LocalStorage-backed profile storage for enrollment.
 *
 * This is the placeholder storage for phase 2a. It lets the app gate
 * the Me tab behind enrollment without yet pulling in SQLite or the
 * Tauri Store plugin. Phase 2b migrates to SQLite so the profile lives
 * next to the voiceprint in Library/Application Support.
 *
 * Schema version bumps invalidate any older profile on read, so the
 * user gets walked through enrollment again after a breaking change
 * instead of seeing a corrupt Me tab.
 */
import type { UserProfile } from "./types";

const STORAGE_KEY = "meridian.profile.v1";

/**
 * Read the enrolled profile from localStorage. Returns null if
 * nothing is stored, if the JSON is malformed, or if the stored
 * version predates the current schema.
 */
export function loadProfile(): UserProfile | null {
  if (typeof window === "undefined") return null;
  const raw = window.localStorage.getItem(STORAGE_KEY);
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw) as UserProfile;
    if (!parsed.name || !Array.isArray(parsed.answers)) return null;
    return parsed;
  } catch {
    return null;
  }
}

export function saveProfile(profile: UserProfile): void {
  if (typeof window === "undefined") return;
  window.localStorage.setItem(STORAGE_KEY, JSON.stringify(profile));
}

export function clearProfile(): void {
  if (typeof window === "undefined") return;
  window.localStorage.removeItem(STORAGE_KEY);
}
